import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Box,
  Button,
  Stack,
  styled,
  SxProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { getFullName } from '@provision/formatting-utils';
import dayjs from 'dayjs';
import React from 'react';

import { EventWrapperProps } from 'react-big-calendar';

import {
  OncallManagementScheduleRecord,
  RecurrenceOptions,
} from 'src/graphql/entities/signal-oncall-schedule/signal-oncall-schedule.types';
import {
  CALENDAR_VIEWS,
  CalendarEventObject,
  CalendarView,
  isCalendarView,
} from 'src/pages/Settings/signal-oncall-schedule/types';
import {
  getAgendaBoundariesInSpecifiedTimezone,
  getDayBoundariesInSpecifiedTimezone,
  getWeekBoundariesInSpecifiedTimezone,
} from './dates-manipulation';
import { getToolbarNavigationDate } from './toolbar-navigation';

function getCalendarHeader(date: Date, timezone: string, view: CalendarView) {
  if (view === 'day') {
    const [dayStart] = getDayBoundariesInSpecifiedTimezone(date, timezone);
    return dayjs(dayStart).format('MMM DD, YYYY');
  }

  if (view === 'agenda') {
    const [agendaStart, agendaEnd] = getAgendaBoundariesInSpecifiedTimezone(date, timezone);
    return `${dayjs(agendaStart).format('MMM DD')} - ${dayjs(agendaEnd).format('MMM DD, YYYY')}`;
  }

  const [weekStart, weekEnd] = getWeekBoundariesInSpecifiedTimezone(date, timezone);
  return `${dayjs(weekStart).format('MMM DD')} - ${dayjs(weekEnd).format('MMM DD, YYYY')}`;
}

const Title = ({
  name,
  prefix,
  icon,
}: {
  name: string;
  prefix: string;
  icon: React.ReactElement;
}) => {
  return (
    <Stack>
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          fontWeight: 700,
        }}
      >
        {icon}
        {prefix}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          margin: 0,
          paddingTop: (theme) => theme.spacing(0.25),
          paddingLeft: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: 400,
        }}
      >
        {name}
      </Typography>
    </Stack>
  );
};

export const SignalHeader = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 700,
}));

export const EventTitle = ({ event }: { event: OncallManagementScheduleRecord }) => {
  if (!event.secondaryAgent) {
    return (
      <Title
        icon={
          <NotificationImportantIcon
            sx={{
              width: '1rem',
              color: (theme) => theme.palette.custom.OV.extended.Signature.color,
            }}
          />
        }
        prefix="Primary"
        name={getFullName(event.primaryAgent)}
      />
    );
  }

  return (
    <Stack spacing={0.5}>
      <Title
        icon={
          <NotificationImportantIcon
            sx={{
              width: '1rem',
              color: (theme) => theme.palette.custom.OV.extended.Signature.color,
            }}
          />
        }
        prefix="Primary"
        name={getFullName(event.primaryAgent)}
      />
      <Title
        icon={
          <NotificationsIcon
            sx={{
              width: '1rem',
              color: (theme) => theme.palette.custom.OV.extended.Signature.color,
            }}
          />
        }
        prefix="Secondary"
        name={getFullName(event.secondaryAgent)}
      />
    </Stack>
  );
};

export const EventWrapper = ({
  children,
  className,
  event,
}: Partial<EventWrapperProps<CalendarEventObject>> & { children: React.ReactNode }) => {
  return (
    <Box
      data-testid="signal-event"
      data-schedule-id={event?.id}
      data-event-start={event?.start instanceof Date ? event.start.toISOString() : undefined}
      data-primary-agent={event?.primaryAgentId}
      sx={{
        '&& .rbc-event': {
          border: `2px solid var(--eventColor)`,
          backgroundColor: `color-mix(in srgb, var(--eventColor) 80%, white)`,
          padding: 0,

          '& .rbc-event-label': {
            backgroundColor: 'var(--eventColor)',
            fontWeight: 700,
            textAlign: 'center',
            padding: '0 0.3rem',
            width: '100%',
            textTransform: 'uppercase',
          },
          '& .rbc-event-content': {
            padding: '0.3rem',
            fontWeight: 700,
          },
          ...(event?.recurrence === RecurrenceOptions.Weekly && {
            '& .rbc-event-label': {
              backgroundImage:
                'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzExOTc2XzQ2NjUpIj4KPHBhdGggZD0iTTEuMjUgMTEuNDk5OEMxLjA1IDExLjQ5OTggMC44NzUgMTEuNDI0OCAwLjcyNSAxMS4yNzQ4QzAuNTc1IDExLjEyNDggMC41IDEwLjk0OTggMC41IDEwLjc0OThWMS4yMzczQzAuNSAxLjA0NTY0IDAuNTc1IDAuODcyNzIxIDAuNzI1IDAuNzE4NTU1QzAuODc1IDAuNTY0Mzg4IDEuMDUgMC40ODczMDUgMS4yNSAwLjQ4NzMwNUgxMC43NjI1QzEwLjk1NDIgMC40ODczMDUgMTEuMTI3MSAwLjU2NDM4OCAxMS4yODEyIDAuNzE4NTU1QzExLjQzNTQgMC44NzI3MjEgMTEuNTEyNSAxLjA0NTY0IDExLjUxMjUgMS4yMzczVjEwLjc0OThDMTEuNTEyNSAxMC45NDk4IDExLjQzNTQgMTEuMTI0OCAxMS4yODEyIDExLjI3NDhDMTEuMTI3MSAxMS40MjQ4IDEwLjk1NDIgMTEuNDk5OCAxMC43NjI1IDExLjQ5OThIMS4yNVpNMy41IDEwLjk5OThMNC4wMjUgMTAuNDQ5OEwyLjk1IDkuMzc0OEg5LjVWNi42MjQ4SDguNzVWOC42MjQ4SDIuOTVMNC4wMjUgNy41NDk4TDMuNSA2Ljk5OThMMS41IDguOTk5OEwzLjUgMTAuOTk5OFpNMi41IDUuMzc0OEgzLjI1VjMuMzc0OEg5LjA1TDcuOTc1IDQuNDQ5OEw4LjUgNC45OTk4TDEwLjUgMi45OTk4TDguNSAwLjk5OTgwNUw3Ljk3NSAxLjU0OThMOS4wNSAyLjYyNDhIMi41VjUuMzc0OFoiIGZpbGw9IiNFRURDRkYiLz4KPC9nPgo8ZGVmcz4KPGNsaXBQYXRoIGlkPSJjbGlwMF8xMTk3Nl80NjY1Ij4KPHJlY3Qgd2lkdGg9IjEyIiBoZWlnaHQ9IjEyIiBmaWxsPSJ3aGl0ZSIvPgo8L2NsaXBQYXRoPgo8L2RlZnM+Cjwvc3ZnPgo=)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'top 1px right 2px',
            },
          }),
        },
      }}
      className={className}
    >
      {children}
    </Box>
  );
};

interface SignalToolbarProps {
  date: Date;
  view: CalendarView;
  availableViews?: readonly CalendarView[];
  onChange: (date: Date) => void;
  onViewChange: (view: CalendarView) => void;
  onAddSchedule: () => void;
  canManageSchedules?: boolean;
  timezone: string;
}

const calendarViewToggleGroupSx: SxProps<Theme> = {
  height: '2.25rem',
  mr: 2,
  '& .MuiToggleButton-root': {
    height: '2.25rem',
    minHeight: '2.25rem',
    minWidth: '4rem',
    p: 1,
    borderColor: (theme) => theme.palette.primary.main,
    color: (theme) => theme.palette.primary.main,
    backgroundColor: 'transparent',
    textTransform: 'uppercase',
    fontSize: (theme) => theme.typography.button.fontSize,
    fontWeight: (theme) => theme.typography.button.fontWeight,
    '&:hover': {
      backgroundColor: (theme) =>
        alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
      borderColor: (theme) => theme.palette.primary.main,
    },
  },
  '& .MuiToggleButton-root.Mui-selected': {
    backgroundColor: (theme) => theme.palette.primary.main,
    borderColor: (theme) => theme.palette.primary.main,
    color: (theme) => theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.primary.main,
      borderColor: (theme) => theme.palette.primary.main,
    },
  },
};
const CALENDAR_VIEW_LABEL: Record<CalendarView, string> = {
  week: 'Week',
  day: 'Day',
  agenda: 'Agenda',
};

export const SignalToolbar = ({
  date,
  view,
  availableViews = CALENDAR_VIEWS,
  onChange,
  onViewChange,
  onAddSchedule,
  canManageSchedules = true,
  timezone,
}: SignalToolbarProps) => {
  const theme = useTheme();

  const selectPrevious = () => {
    const nextDate = getToolbarNavigationDate(date, view, 'previous');
    onChange(nextDate);
  };
  const selectNext = () => {
    const nextDate = getToolbarNavigationDate(date, view, 'next');
    onChange(nextDate);
  };
  const selectToday = () => {
    onChange(dayjs().startOf('day').toDate());
  };

  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        rowGap: { xs: 1, sm: 0 },
        py: 1,
        px: { xs: 0, sm: 1 },
      }}
    >
      <Stack
        direction="row"
        gap={{ xs: '0.375rem', sm: '0.5rem' }}
        sx={{ p: { xs: 0, sm: 1 } }}
      >
        <Button
          variant="outlined"
          size="medium"
          sx={{
            minWidth: { xs: '2rem', sm: 0 },
            height: { xs: '2rem', sm: 'auto' },
            padding: { xs: 0.5, sm: 1 },
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1.125rem', sm: '1.5rem' },
            },
          }}
          aria-label="Previous period"
          onClick={selectPrevious}
        >
          <NavigateBeforeIcon />
        </Button>
        <Button
          variant="outlined"
          size="medium"
          sx={{
            minWidth: { xs: '2rem', sm: 0 },
            height: { xs: '2rem', sm: 'auto' },
            padding: { xs: 0.5, sm: 1 },
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1.125rem', sm: '1.5rem' },
            },
          }}
          aria-label="Next period"
          onClick={selectNext}
        >
          <NavigateNextIcon />
        </Button>
        <Button
          variant="outlined"
          size="medium"
          sx={{
            height: { xs: '2rem', sm: 'auto' },
            minWidth: { xs: '3.25rem', sm: 'auto' },
            padding: { xs: '0.25rem 0.5rem', sm: 1 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
          onClick={selectToday}
        >
          Today
        </Button>
      </Stack>
      <Typography
        variant="h6"
        sx={{
          fontSize: { xs: '1.05rem', sm: '1.25rem' },
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}
      >
        {getCalendarHeader(date, timezone, view)}
      </Typography>
      <Stack
        direction="row"
        gap="0.5rem"
        alignItems="center"
        sx={{
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="medium"
          value={view}
          sx={{ ...calendarViewToggleGroupSx, mr: { xs: 0, sm: 2 } }}
          onChange={(_, nextView) => {
            if (isCalendarView(nextView) && availableViews.includes(nextView)) {
              onViewChange(nextView);
            }
          }}
        >
          {CALENDAR_VIEWS.filter((calendarView) => availableViews.includes(calendarView)).map(
            (calendarView) => (
              <ToggleButton key={calendarView} value={calendarView}>
                {CALENDAR_VIEW_LABEL[calendarView]}
              </ToggleButton>
            ),
          )}
        </ToggleButtonGroup>
        {canManageSchedules ? (
          <Button
            data-testid="signal-add-schedule"
            variant="contained"
            size="medium"
            sx={{ px: 2 }}
            onClick={onAddSchedule}
          >
            Add Schedule
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
};

export const CalendarWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    '& .rbc-agenda-view > table.rbc-agenda-table, & .rbc-agenda-view .rbc-agenda-content table.rbc-agenda-table':
      {
        minWidth: '19rem',
        tableLayout: 'fixed',
      },
    '& .rbc-agenda-view > table.rbc-agenda-table .rbc-header': {
      whiteSpace: 'nowrap',
    },
    '& .rbc-agenda-view > table.rbc-agenda-table .rbc-header:nth-of-type(1), & .rbc-agenda-view .rbc-agenda-date-cell':
      {
        width: '5.75rem',
        minWidth: '5.75rem',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        lineHeight: 1.2,
      },
    '& .rbc-agenda-view > table.rbc-agenda-table .rbc-header:nth-of-type(2), & .rbc-agenda-view .rbc-agenda-time-cell':
      {
        width: '6.25rem',
        minWidth: '6.25rem',
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        lineHeight: 1.2,
      },
    '& .rbc-agenda-view > table.rbc-agenda-table .rbc-header:last-of-type, & .rbc-agenda-view .rbc-agenda-event-cell':
      {
        minWidth: '7rem',
      },
    '& .rbc-agenda-view .rbc-agenda-event-cell': {
      overflowWrap: 'break-word',
    },
    '& .rbc-calendar': {
      flex: 1,
      height: '100%',
      minHeight: 0,
    },
    '& .rbc-time-view': {
      minHeight: 0,
    },
    '& .rbc-time-content': {
      minHeight: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      touchAction: 'pan-y',
      overscrollBehaviorY: 'contain',
    },
  },
  '& .rbc-agenda-view': {
    overflow: 'auto',
  },
  '& .rbc-agenda-view > table.rbc-agenda-table': {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
  },
  '& .rbc-agenda-view table.rbc-agenda-table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    borderColor: theme.palette.divider,
  },
  '& .rbc-agenda-view table.rbc-agenda-table thead th': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
  },
  '& .rbc-agenda-view table.rbc-agenda-table tbody > tr > td': {
    borderColor: theme.palette.divider,
  },
  '& .rbc-agenda-view .rbc-agenda-content table.rbc-agenda-table tbody > tr > td': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  '& .rbc-agenda-event-cell': {
    fontWeight: 600,
    cursor: 'pointer',
  },
  '& .rbc-today': {
    backgroundColor: 'rgba(162, 89, 255, 0.05)',
  },
  '& .rbc-day-slot .rbc-time-slot': {
    border: 'none',
  },
  '& .rbc-current-time-indicator': {
    backgroundColor: theme.palette.primary.main,
  },

  '& .rbc-allday-cell': {
    cursor: 'pointer',
    pointerEvents: 'none',

    '& .rbc-day-bg': {
      pointerEvents: 'auto',
      cursor: 'pointer',

      '&:hover': {
        backgroundColor: 'rgba(162, 89, 255, 0.2)',
      },

      '&.rbc-selected-cell:not(:hover)': {
        background: 'none',
      },
    },
  },

  '& .rbc-day-slot .rbc-time-slot.rbc-missing-hour-slot-start': {
    position: 'relative',
    overflow: 'visible',
  },
  '& .rbc-day-slot .rbc-time-slot.rbc-missing-hour-slot-start::before': {
    content: '"DST Time Shift"',
    textAlign: 'center',
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    zIndex: 0,
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    pointerEvents: 'none',
  },
  '& .rbc-day-slot .rbc-time-slot.rbc-missing-hour-slot-start::after': {
    content: '""',
    position: 'absolute',
    textAlign: 'center',
    inset: 0,
    height: '200%',
    backgroundImage: `repeating-linear-gradient(
      -45deg,
      rgb(219 169 99 / 20%) 0 6px,
      transparent 6px 12px
    )`,
    pointerEvents: 'none',
  },
  // In react-big-calendar TimeGutter, grouped slots are generated first, then each rendered slot
  // is DST-adjusted via `localizer.getDstOffset(previousSlot, slot)`. On spring-forward days this
  // can produce two adjacent groups that both resolve to the same visible "start" hour (e.g. 3:00).
  // Hide pseudo-content for the second adjacent start group so only one DST placeholder is shown.
  '& .rbc-day-slot .rbc-timeslot-group:has(.rbc-missing-hour-slot-start) + .rbc-timeslot-group:has(.rbc-missing-hour-slot-start) .rbc-time-slot.rbc-missing-hour-slot-start::before':
    {
      content: 'none',
    },
  '& .rbc-day-slot .rbc-timeslot-group:has(.rbc-missing-hour-slot-start) + .rbc-timeslot-group:has(.rbc-missing-hour-slot-start) .rbc-time-slot.rbc-missing-hour-slot-start::after':
    {
      content: 'none',
    },
  '& .rbc-day-slot .rbc-time-slot.rbc-missing-hour-slot-follow': {
    background: 'none',
  },
}));
